import React, { useState, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Form } from '@/components/ui/form';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { useEstimateLineItems } from './line-items/hooks/useEstimateLineItems';
import { useLocalLineItems } from './line-items/hooks/useLocalLineItems';
import { useLineItemMutations } from './line-items/hooks/useLineItemMutations';
import { useQuantityEditor } from './line-items/hooks/useQuantityEditor';
import { useEstimateFinalStep } from './hooks/useEstimateFinalStep';
import { useEstimateChangeDetection } from './hooks/useEstimateChangeDetection';
import { useEstimateCommit } from './hooks/useEstimateCommit';
import { useShoppingCartState } from './line-items/hooks/useShoppingCartState';
import { useCatalogSearch } from './line-items/hooks/useCatalogSearch';
import { useBrowseCategories } from './line-items/hooks/useBrowseCategories';
import { useSystemItemPriceChanges } from './line-items/hooks/useSystemItemPriceChanges';
import { useAuth } from '@/contexts/useAuth';

import CatalogGrid from './line-items/components/CatalogGrid';
import CatalogLeftPanel from './line-items/components/CatalogLeftPanel';
import ShoppingCartSidebar from './line-items/components/ShoppingCartSidebar';
import EstimateFinalStepFields from './components/EstimateFinalStepFields';
import VersioningWarningDialog from './dialogs/VersioningWarningDialog';
import SendVersionDialog from './dialogs/SendVersionDialog';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ChevronDown, Settings2, AlertCircle, ShoppingCart, Plus, Calculator } from 'lucide-react';
import { AdjustmentSettingsPanel } from './adjustments/AdjustmentSettingsPanel';


import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from '@/components/ui/drawer';
import { AdjustmentSummaryCard } from './adjustments/AdjustmentSummaryCard';
import { useAdjustmentSettings } from '@/hooks/useAdjustmentSettings';
import { useNotify } from '@/hooks/useNotify';
import InMemoryCostAnalysisModal from './InMemoryCostAnalysisModal';
import AddCustomItemForm from './components/AddCustomItemForm';
import { useCustomLineItem } from './hooks/useCustomLineItem';
import { supabase } from '@/integrations/supabase/client';
import { AdjustmentSettings } from '@/types/adjustment-filters';
import { Alert, AlertDescription } from '@/components/ui/alert';
import EstimateProgress from './components/EstimateProgress';
import FloatingTotalSummary from './components/FloatingTotalSummary';
import useEstimateValidation from './hooks/useEstimateValidation';
import ValidationWarnings from './components/ValidationWarnings';
import PageLayout from '@/components/layout/PageLayout';


interface EstimateLineItemsProps {
  estimateId: string;
}

const EstimateLineItems: React.FC<EstimateLineItemsProps> = ({ estimateId }) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const notify = useNotify();
  const [showFinalStep, setShowFinalStep] = useState(false);
  const [isSearchScopedToCategory, setIsSearchScopedToCategory] = useState(true);
  const [showSystemItems, setShowSystemItems] = useState(true);
  const [classificationMap, setClassificationMap] = useState<Record<string, string>>({});
  
  const [showCostAnalysis, setShowCostAnalysis] = useState(false);
  const [showAddCustomItem, setShowAddCustomItem] = useState(false);
  const [isAddingItem, setIsAddingItem] = useState<string | null>(null);
  const [isRemovingItem, setIsRemovingItem] = useState(false);
  const [isUpdatingQuantity, setIsUpdatingQuantity] = useState(false);
  
  // Versioning dialog states
  const [showVersioningWarning, setShowVersioningWarning] = useState(false);
  const [showSendVersionDialog, setShowSendVersionDialog] = useState(false);
  const [pendingFormData, setPendingFormData] = useState<any>(null);
  const [pendingWarningData, setPendingWarningData] = useState<any>(null);
  const [pendingVersionData, setPendingVersionData] = useState<{
    versionId: string;
    versionType: 'estimate' | 'change_order';
    versionNumber: number;
  } | null>(null);
  
  // Baseline snapshots for change detection
  const [baselineAdjustments, setBaselineAdjustments] = useState<AdjustmentSettings>({
    filters: [],
    discountPercentage: undefined,
    discountType: 'percentage'
  });
  const [baselineFinalStepFields, setBaselineFinalStepFields] = useState({
    special_instructions: '',
    payment_terms: '',
    validity_period: 14
  });
  
  // Floating summary expanded state - moved here to be before early returns
  const [summaryExpanded, setSummaryExpanded] = useState(false);
  
  const { isDrawerOpen, setDrawerOpen, isFilterSheetOpen, setFilterSheetOpen } = useShoppingCartState();
  const { addCustomLineItem } = useCustomLineItem({ estimateId });

  console.log('[EstimateLineItems] Component rendering with estimateId:', estimateId);

  // Fetch data from DB (read-only reference)
  const {
    estimate,
    lineItems: dbLineItems,
    versionSnapshot,
    isLoading,
    errors,
  } = useEstimateLineItems(estimateId);

  // Get current status for determining edit mode
  const currentStatus = estimate?.status || 'draft';
  
  // Status-based edit behavior:
  // - Draft: Direct DB writes (autosave OK)
  // - Sent/Approved/Rejected/Expired: Local-first editing, prompts for new version/change order
  // - Cancelled: Completely locked - no editing allowed
  const isLockedStatus = currentStatus === 'cancelled';
  const isLocalFirstMode = ['sent', 'approved', 'rejected', 'expired'].includes(currentStatus);

  // DB mutations for draft mode (write-through / autosave)
  const {
    addLineItemMutation,
    removeLineItemMutation,
    updateQuantityMutation,
  } = useLineItemMutations(estimateId, dbLineItems || []);

  // LOCAL-FIRST: Manage line items locally (for sent/approved)
  const {
    localLineItems,
    allLocalLineItems,
    baselineSnapshot: lineItemsBaseline,
    isInitialized,
    addItemLocally,
    addCustomItemLocally,
    removeItemLocally,
    updateQuantityLocally,
    hasLineItemChanges,
  } = useLocalLineItems({
    estimateId,
    initialLineItems: dbLineItems,
    isLoading,
  });

  // Choose which line items to display based on mode
  const displayLineItems = isLocalFirstMode ? localLineItems : dbLineItems;
  
  // Validation - must be before early returns
  const validation = useEstimateValidation(estimate, displayLineItems);

  // Change detection
  const { detectChanges } = useEstimateChangeDetection();
  
  // Commit changes to DB
  const { isCommitting, commitChanges } = useEstimateCommit();

  // Quantity editor - uses different mutation based on mode
  const {
    editingItem,
    startEditingQuantity,
    cancelQuantityEdit,
    saveQuantityEdit,
    updateEditingQuantity,
  } = useQuantityEditor({
    mutate: async (params: any) => {
      setIsUpdatingQuantity(true);
      try {
        if (isLocalFirstMode) {
          // Local-first mode: update locally
          await updateQuantityLocally(
            params.lineItemId,
            params.quantity,
            params.componentType,
            params.componentId,
            params.baseCost
          );
          notify.success('Quantity updated');
        } else {
          // Draft mode: write to DB immediately
          await updateQuantityMutation.mutateAsync(params);
        }
      } finally {
        setIsUpdatingQuantity(false);
      }
    },
    isPending: isUpdatingQuantity,
  });

  // Final step form
  const {
    form: finalStepForm,
    isLoading: isFinalStepLoading,
    isInitialLoading: isFinalStepInitialLoading,
    estimateStatus,
    createVersionAndComplete,
    sendVersion,
  } = useEstimateFinalStep(estimateId);

  // Adjustment settings (still uses local state, only saves on commit)
  const {
    settings: adjustmentSettings,
    isLoading: isLoadingAdjustments,
    updateSettings: updateAdjustmentSettings,
    hasAdjustments
  } = useAdjustmentSettings({ estimateId });

  // Store baseline adjustments when loaded
  useEffect(() => {
    if (!isLoadingAdjustments && adjustmentSettings) {
      setBaselineAdjustments(JSON.parse(JSON.stringify(adjustmentSettings)));
    }
  }, [isLoadingAdjustments]);

  // Store baseline final step fields when form is loaded
  useEffect(() => {
    if (!isFinalStepInitialLoading) {
      const values = finalStepForm.getValues();
      setBaselineFinalStepFields({
        special_instructions: values.special_instructions || '',
        payment_terms: values.payment_terms || '',
        validity_period: values.validity_period || 14
      });
    }
  }, [isFinalStepInitialLoading, finalStepForm]);

  // BASELINE RESET: Capture fresh baselines when entering finalize step for sent/approved
  // This ensures we have accurate baselines even if user navigated away and back
  const prevShowFinalStep = useRef(showFinalStep);
  useEffect(() => {
    // Detect transition from line items view to finalize step
    if (showFinalStep && !prevShowFinalStep.current && isLocalFirstMode) {
      console.log('[EstimateLineItems] Entering finalize step for sent/approved estimate - capturing fresh baselines');
      
      // Capture current adjustment settings as baseline
      if (adjustmentSettings) {
        setBaselineAdjustments(JSON.parse(JSON.stringify(adjustmentSettings)));
      }
      
      // Capture current form values as baseline
      const values = finalStepForm.getValues();
      setBaselineFinalStepFields({
        special_instructions: values.special_instructions || '',
        payment_terms: values.payment_terms || '',
        validity_period: values.validity_period || 14
      });
    }
    prevShowFinalStep.current = showFinalStep;
  }, [showFinalStep, isLocalFirstMode, adjustmentSettings, finalStepForm]);

  // --- New catalog data layer ---
  const { profile } = useAuth();
  const companyId = profile?.company_id ?? null;

  const catalogSearch = useCatalogSearch(companyId);
  const { buildPath } = useBrowseCategories();
  useSystemItemPriceChanges(estimateId, currentStatus === 'draft');

  // Fetch show_system_items company setting
  useEffect(() => {
    if (!companyId) return;
    supabase
      .from('cost_center_settings')
      .select('show_system_items')
      .eq('company_id', companyId)
      .single()
      .then(({ data }) => {
        if (data?.show_system_items !== undefined) {
          setShowSystemItems(data.show_system_items);
          if (!data.show_system_items) {
            catalogSearch.updateFilter('include_system', false);
          }
        }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  // Fetch classification categories for the tile badge labels
  useEffect(() => {
    supabase
      .from('supplier_categories')
      .select('id, name')
      .eq('is_default', true)
      .then(({ data }) => {
        if (data) {
          const map: Record<string, string> = {};
          data.forEach(c => { map[c.id] = c.name; });
          setClassificationMap(map);
        }
      });
  }, []);

  // IDs of source catalog items already in the estimate cart
  const cartItemIds = (isLocalFirstMode ? localLineItems : dbLineItems || [])
    .map((item: any) => item.component_id)
    .filter(Boolean) as string[];

  // Full path string for the currently selected browse category
  const browseCategoryPath = catalogSearch.filters.browse_category_id
    ? buildPath(catalogSearch.filters.browse_category_id)
    : null;

  // Active filter count for the panel badge
  const activeFiltersCount =
    (catalogSearch.filters.browse_category_id ? 1 : 0) +
    (catalogSearch.filters.item_types ? catalogSearch.filters.item_types.length : 0) +
    (catalogSearch.filters.trade_id ? 1 : 0) +
    (catalogSearch.filters.classification_category_id ? 1 : 0) +
    (!catalogSearch.filters.include_system ? 1 : 0);

  // Show loading if data is loading OR if local-first mode and not yet initialized
  const isFullyReady = !isLoading && (!isLocalFirstMode || isInitialized);
  
  if (!isFullyReady) {
    console.log('[EstimateLineItems] Still loading...', { isLoading, isLocalFirstMode, isInitialized });
    return (
      <PageLayout>
        <div className="flex items-center justify-center h-64">
          <div className="flex flex-col items-center gap-2">
            <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            <p className="text-muted-foreground">Loading estimate details...</p>
          </div>
        </div>
      </PageLayout>
    );
  }

  // Redirect or show read-only view for cancelled estimates
  if (isLockedStatus) {
    return (
      <PageLayout>
        <div className="flex flex-col items-center justify-center h-64 gap-4">
          <AlertCircle className="h-12 w-12 text-muted-foreground" />
          <h2 className="text-xl font-semibold font-montserrat">Estimate is Cancelled</h2>
          <p className="text-muted-foreground text-center max-w-md">
            This estimate has been cancelled and cannot be edited. You can view its details but no changes are allowed.
          </p>
          <Button variant="outline" onClick={() => navigate(`/estimates/${estimateId}`)}>
            View Estimate Details
          </Button>
        </div>
      </PageLayout>
    );
  }

  if (errors.estimateError) {
    console.error('[EstimateLineItems] Estimate error:', errors.estimateError);
    return (
      <PageLayout>
        <div className="flex flex-col items-center justify-center h-64 gap-4">
          <AlertCircle className="h-12 w-12 text-destructive" />
          <h2 className="text-xl font-semibold font-montserrat">Error Loading Estimate</h2>
          <p className="text-muted-foreground text-center max-w-md">
            {String(errors.estimateError)}
          </p>
          <Button variant="outline" onClick={() => navigate('/estimates')}>
            Back to Estimates
          </Button>
        </div>
      </PageLayout>
    );
  }

  if (!estimate) {
    console.log('[EstimateLineItems] No estimate found');
    return (
      <PageLayout>
        <div className="flex flex-col items-center justify-center h-64 gap-4">
          <AlertCircle className="h-12 w-12 text-muted-foreground" />
          <h2 className="text-xl font-semibold font-montserrat">Estimate Not Found</h2>
          <p className="text-muted-foreground text-center max-w-md">
            The estimate you're looking for doesn't exist or has been removed.
          </p>
          <Button variant="outline" onClick={() => navigate('/estimates')}>
            Back to Estimates
          </Button>
        </div>
      </PageLayout>
    );
  }

  console.log('[EstimateLineItems] Rendering main component, mode:', isLocalFirstMode ? 'local-first' : 'write-through');

  // HYBRID: Add item - DB write for draft, local for sent/approved
  const handleAddItem = async (item: any, componentType: string) => {
    console.log('[EstimateLineItems] Adding item:', { item, componentType, mode: isLocalFirstMode ? 'local' : 'db' });
    const itemKey = `${componentType}-${item.id}`;
    setIsAddingItem(itemKey);
    
    try {
      let mappedComponentType = componentType;
      if (componentType === 'internal') {
        mappedComponentType = 'internal_line_item';
      } else if (componentType === 'supplier') {
        mappedComponentType = 'supplier_line_item';
      } else if (componentType === 'assembly') {
        mappedComponentType = 'assembly';
      } else if (componentType === 'system') {
        mappedComponentType = 'system_line_item';
      }
      
      if (isLocalFirstMode) {
        // Local-first mode: save locally
        const result = await addItemLocally(item, mappedComponentType);
        if (result.isUpdate) {
          notify.success('Item quantity updated');
        } else {
          notify.success('Item added to estimate');
        }
      } else {
        // Draft mode: write to DB immediately
        await addLineItemMutation.mutateAsync({ item, componentType: mappedComponentType });
      }
    } finally {
      setIsAddingItem(null);
    }
  };

  // HYBRID: Update quantity - DB write for draft, local for sent/approved
  const handleUpdateQuantity = async (itemId: string, quantity: number) => {
    const itemList = isLocalFirstMode ? localLineItems : dbLineItems;
    const item = itemList?.find(li => li.id === itemId);
    if (item) {
      setIsUpdatingQuantity(true);
      try {
        if (isLocalFirstMode) {
          await updateQuantityLocally(
            itemId,
            quantity,
            item.component_type,
            item.component_id,
            item.unit_cost_snapshot
          );
          notify.success('Quantity updated');
        } else {
          await updateQuantityMutation.mutateAsync({
            lineItemId: itemId,
            quantity,
            componentType: item.component_type,
            componentId: item.component_id,
            baseCost: item.unit_cost_snapshot
          });
        }
      } finally {
        setIsUpdatingQuantity(false);
      }
    }
  };

  // HYBRID: Remove item - DB write for draft, local for sent/approved
  const handleRemoveItem = (itemId: string) => {
    setIsRemovingItem(true);
    if (isLocalFirstMode) {
      removeItemLocally(itemId);
      notify.success('Item removed from estimate');
    } else {
      removeLineItemMutation.mutate(itemId);
    }
    setIsRemovingItem(false);
  };

  // COMPLETE ESTIMATE: This is where we check status and persist if appropriate
  const onFinalStepSubmit = async (data: any) => {
    // Get current form values for change detection
    const currentFinalStepFields = {
      special_instructions: data.special_instructions || '',
      payment_terms: data.payment_terms || '',
      validity_period: data.validity_period || 14
    };

    // Detect all changes
    const changeResult = detectChanges(
      {
        lineItems: lineItemsBaseline,
        adjustmentSettings: baselineAdjustments,
        finalStepFields: baselineFinalStepFields
      },
      {
        localLineItems: allLocalLineItems,
        adjustmentSettings,
        finalStepFields: currentFinalStepFields
      }
    );

    console.log('[EstimateLineItems] Change detection result:', changeResult);

    // Re-fetch current status from DB to ensure we have the latest
    const { data: currentEstimate, error: fetchError } = await supabase
      .from('estimates')
      .select('status')
      .eq('id', estimateId)
      .single();

    if (fetchError) {
      notify.error('Failed to verify estimate status');
      return;
    }

    const currentStatus = currentEstimate?.status || 'draft';
    console.log('[EstimateLineItems] Current DB status:', currentStatus);

    // If no changes, still update expires_at for drafts to keep it current
    if (!changeResult.hasChanges) {
      if (currentStatus === 'draft') {
        const validityPeriod = finalStepForm.getValues('validity_period') || 14;
        const { error: expiresError } = await supabase
          .from('estimates')
          .update({
            expires_at: new Date(Date.now() + (validityPeriod * 24 * 60 * 60 * 1000)).toISOString()
          })
          .eq('id', estimateId);

      }
      notify.info('No changes detected');
      navigate(`/estimates/${estimateId}`);
      return;
    }

    // DRAFT: Persist changes directly
    if (currentStatus === 'draft') {
      const result = await commitChanges({
        estimateId,
        localLineItems: allLocalLineItems,
        baselineSnapshot: lineItemsBaseline,
        adjustmentSettings,
        finalStepFields: currentFinalStepFields,
      });

      if (result.success) {
        notify.success('Estimate updated successfully');
        navigate(`/estimates/${estimateId}`);
      } else {
        notify.error(result.error || 'Failed to save changes');
      }
      return;
    }

    // SENT/REJECTED/EXPIRED: Directional validity checking + versioning warning
    if (['sent', 'rejected', 'expired'].includes(currentStatus)) {
      const validityChanged = currentFinalStepFields.validity_period !== baselineFinalStepFields.validity_period;

      if (validityChanged) {
        const sentDate = versionSnapshot?.sent_date
          ? new Date(versionSnapshot.sent_date)
          : new Date(estimate?.created_at || Date.now());
        const newExpiresAt = new Date(
          sentDate.getTime() + (currentFinalStepFields.validity_period * 24 * 60 * 60 * 1000)
        );
        const currentExpiresAt = estimate?.expires_at ? new Date(estimate.expires_at) : null;

        // Lengthening or equal: update directly without versioning
        if (!currentExpiresAt || newExpiresAt >= currentExpiresAt) {
          const { error: updateError } = await supabase
            .from('estimates')
            .update({
              validity_period: currentFinalStepFields.validity_period,
              expires_at: newExpiresAt.toISOString(),
              updated_at: new Date().toISOString(),
            })
            .eq('id', estimateId);

          if (updateError) {
            notify.error('Failed to update validity period');
            return;
          }

          queryClient.invalidateQueries({ queryKey: ['estimate', estimateId] });
          queryClient.invalidateQueries({ queryKey: ['estimateVersions', estimateId] });
          notify.success('Estimate validity period updated');
          navigate(`/estimates/${estimateId}`);
          return;
        }

        // Shortening: show versioning dialog with specific message
        setPendingFormData({
          ...data,
          localLineItems: allLocalLineItems,
          adjustmentSettings,
        });
        setPendingWarningData({
          warningType: 'new_version',
          message: `This estimate has already been sent to the customer with an expiry date of ${format(currentExpiresAt, 'dd MMMM yyyy')}. Shortening it to ${format(newExpiresAt, 'dd MMMM yyyy')} may affect the customer who is expecting it to be valid until the original date. This will create a new version.`,
          changeResult
        });
        setShowVersioningWarning(true);
        return;
      }

      // Validity did not change but other fields changed: generic warning
      setPendingFormData({
        ...data,
        localLineItems: allLocalLineItems,
        adjustmentSettings,
      });
      setPendingWarningData({
        warningType: 'new_version',
        message: `This estimate is ${currentStatus}. Completing with changes will create a new version.`,
        changeResult
      });
      setShowVersioningWarning(true);
      return;
    }

    // APPROVED: Show versioning warning for change order
    if (currentStatus === 'approved') {
      setPendingFormData({
        ...data,
        localLineItems: allLocalLineItems,
        adjustmentSettings,
      });
      setPendingWarningData({
        warningType: 'change_order',
        message: 'This estimate has been approved by the client. Completing with changes will create a change order that maintains links to existing invoices and payments.',
        changeResult
      });
      setShowVersioningWarning(true);
      return;
    }

    // Fallback for any other statuses - commit directly
    const result = await commitChanges({
      estimateId,
      localLineItems: allLocalLineItems,
      baselineSnapshot: lineItemsBaseline,
      adjustmentSettings,
      finalStepFields: currentFinalStepFields,
    });

    if (result.success) {
      notify.success('Estimate updated successfully');
      navigate(`/estimates/${estimateId}`);
    } else {
      notify.error(result.error || 'Failed to save changes');
    }
  };

  const handleVersioningProceed = async (reason?: string, description?: string) => {
    if (!pendingFormData || !pendingWarningData) return;

    try {
      const versionType = pendingWarningData.warningType === 'change_order' ? 'change_order' : 'estimate';
      
      // Prepare pending changes to pass to createVersionAndComplete
      const pendingChanges = {
        localLineItems: pendingFormData.localLineItems,
        baselineSnapshot: lineItemsBaseline,
        adjustmentSettings: pendingFormData.adjustmentSettings,
        finalStepFields: {
          special_instructions: pendingFormData.special_instructions || '',
          payment_terms: pendingFormData.payment_terms || '',
          validity_period: pendingFormData.validity_period || 14
        }
      };

      // ATOMIC: createVersionAndComplete now handles both commit and version creation
      // This prevents partial persistence if version creation fails
      const versionResponse = await createVersionAndComplete(
        pendingFormData,
        versionType,
        reason,
        description,
        (versionId, versionType, versionNumber) => {
          console.log('[EstimateLineItems] Version created callback:', { versionId, versionType, versionNumber });
          if (versionId) {
            setPendingVersionData({ versionId, versionType, versionNumber });
            setShowSendVersionDialog(true);
          } else {
            console.error('[EstimateLineItems] Version created but versionId is empty!');
            notify.error('Failed to get version ID');
          }
        },
        pendingChanges // Pass pending changes for atomic operation
      );

      setShowVersioningWarning(false);
      setPendingFormData(null);
      setPendingWarningData(null);
      
    } catch (error) {
      console.error('Error creating version:', error);
      notify.error('Failed to create version');
    }
  };

  // Navigate to estimate page with sendVersion param to open email dialog
  const handleSendVersion = async () => {
    if (!pendingVersionData) {
      console.error('[handleSendVersion] No pending version data');
      return;
    }
    
    if (!pendingVersionData.versionId) {
      console.error('[handleSendVersion] Version ID is empty:', pendingVersionData);
      notify.error('Unable to send: Version ID is missing');
      return;
    }

    // Navigate to estimate page with query param to auto-open send dialog
    setShowSendVersionDialog(false);
    setPendingVersionData(null);
    navigate(`/estimates/${estimateId}?sendVersion=${pendingVersionData.versionId}`);
  };

  const handleSaveDraft = () => {
    setShowSendVersionDialog(false);
    setPendingVersionData(null);
    navigate(`/estimates/${estimateId}`);
  };

  const handleVersioningCancel = () => {
    setShowVersioningWarning(false);
    setPendingFormData(null);
    setPendingWarningData(null);
    navigate(`/estimates/${estimateId}`);
  };

  // Calculate totals for display
  const cartTotal = (displayLineItems || []).reduce((sum, item) => sum + (item.total_cost_snapshot || 0), 0);
  const cartItemCount = (displayLineItems || []).length;

  if (showFinalStep) {
    return (
      <div className="space-y-4 pb-24 md:pb-6">
        {/* Progress Indicator */}
        <EstimateProgress 
          currentStep="finalize" 
          completedSteps={['details', 'items']}
          onStepClick={(step) => {
            if (step === 'items') setShowFinalStep(false);
            if (step === 'details') navigate(`/estimates/edit/${estimateId}`);
          }}
        />

        <div>
          <h1 className="text-2xl font-bold">Finalize Estimate</h1>
          <p className="text-muted-foreground">
            Complete the final details for: {estimate.title}
          </p>
        </div>
        
        {/* Validation Warnings */}
        <ValidationWarnings 
          errors={validation.errors} 
          warnings={validation.warnings}
          showSuccess={validation.isValid}
        />

        <Card className="max-w-4xl mx-auto overflow-hidden">
          <CardHeader>
            <CardTitle>Final Estimate Details</CardTitle>
          </CardHeader>
          <CardContent>
            {!isFinalStepInitialLoading && (
              <Form {...finalStepForm}>
                <form onSubmit={finalStepForm.handleSubmit(onFinalStepSubmit)} className="space-y-6">
                  <EstimateFinalStepFields form={finalStepForm} sentDate={versionSnapshot?.sent_date} createdAt={estimate?.created_at} estimateStatus={estimate?.status} />
                  
                  {/* Cost Analysis & Adjustments Section */}
                  <Collapsible defaultOpen className="border rounded-lg">
                    <CollapsibleTrigger className="flex w-full items-center justify-between p-4 hover:bg-accent">
                      <div className="flex items-center gap-2">
                        <Settings2 className="h-5 w-5" />
                        <div className="text-left">
                          <h3 className="font-semibold">Cost Analysis & Adjustments</h3>
                          <p className="text-sm text-muted-foreground">
                            Remove markups and apply discounts (non-destructive)
                          </p>
                        </div>
                      </div>
                      <ChevronDown className="h-4 w-4 transition-transform duration-200" />
                    </CollapsibleTrigger>
                    <CollapsibleContent className="p-4 pt-0 space-y-4">
                      {isLoadingAdjustments ? (
                        <div className="flex items-center justify-center py-8">
                          <p className="text-muted-foreground">Loading adjustments...</p>
                        </div>
                      ) : (
                        <>
                          <div className="flex justify-end mb-4">
                            <Button
                              type="button"
                              variant="outline"
                              onClick={() => setShowCostAnalysis(true)}
                              className="w-full sm:w-auto"
                            >
                              <Calculator className="mr-2 h-4 w-4" />
                              Cost Analysis
                            </Button>
                          </div>
                          
                          <AdjustmentSettingsPanel 
                            settings={adjustmentSettings} 
                            onChange={updateAdjustmentSettings} 
                          />
                          
                          {hasAdjustments && localLineItems && localLineItems.length > 0 && (
                            <AdjustmentSummaryCard 
                              lineItems={localLineItems} 
                              adjustmentSettings={adjustmentSettings} 
                            />
                          )}
                        </>
                      )}
                    </CollapsibleContent>
                  </Collapsible>
                  
                  <div className="flex justify-between space-x-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setShowFinalStep(false)}
                    >
                      Back to Line Items
                    </Button>
                    <Button type="submit" disabled={isFinalStepLoading || isCommitting}>
                      {isCommitting ? 'Saving...' : isFinalStepLoading ? 'Processing...' : 'Complete Estimate'}
                    </Button>
                  </div>
                </form>
              </Form>
            )}
          </CardContent>
        </Card>

        {/* Versioning Dialogs */}
        <VersioningWarningDialog
          isOpen={showVersioningWarning}
          onClose={handleVersioningCancel}
          onProceed={handleVersioningProceed}
          warningType={pendingWarningData?.warningType || 'new_version'}
          message={pendingWarningData?.message || ''}
          isLoading={isFinalStepLoading || isCommitting}
        />

        <SendVersionDialog
          isOpen={showSendVersionDialog}
          onClose={() => setShowSendVersionDialog(false)}
          onSend={handleSendVersion}
          onSaveDraft={handleSaveDraft}
          versionType={pendingVersionData?.versionType || 'estimate'}
          versionNumber={pendingVersionData?.versionNumber || 1}
          isLoading={isFinalStepLoading}
        />

        {/* Cost Analysis Modal - Uses local line items */}
        <InMemoryCostAnalysisModal
          lineItems={displayLineItems}
          isOpen={showCostAnalysis}
          onClose={() => setShowCostAnalysis(false)}
          estimateStatus={estimateStatus}
          estimateId={estimateId}
          discountPercentage={adjustmentSettings?.discountPercentage}
          adjustmentFilters={adjustmentSettings?.filters}
        />
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col max-w-full pb-20 md:pb-0">
      {/* Header with Progress */}
      <div className="border-b bg-background px-4 md:px-6 py-3 md:py-4 flex-shrink-0 space-y-3 md:space-y-4">
        {/* Progress Indicator */}
        <EstimateProgress 
          currentStep="items" 
          completedSteps={['details']}
          onStepClick={(step) => {
            if (step === 'details') navigate(`/estimates/edit/${estimateId}`);
          }}
        />
        
        <div className="flex items-center justify-between">
          <div className="min-w-0 flex-1 mr-4">
            <h1 className="text-lg md:text-2xl font-bold truncate">Add Items</h1>
            <p className="text-muted-foreground truncate text-xs md:text-base">
              {estimate.title}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowAddCustomItem(true)}
            >
              <Plus className="h-4 w-4 mr-1" />
              Custom Item
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/estimates')}
              className="hidden md:flex"
            >
              Back to Estimates
            </Button>
          </div>
        </div>
        
        {/* Edit mode indicator for non-draft estimates */}
        {isLocalFirstMode && (
          <Alert className="mt-2 md:mt-4">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription className="text-xs md:text-sm">
              <strong>Editing {currentStatus} estimate:</strong> Changes are saved locally and will create a {currentStatus === 'approved' ? 'change order' : 'new version'} when you complete.
            </AlertDescription>
          </Alert>
        )}
      </div>

      {/* Main Content — Left panel + catalog grid */}
      <div className="flex-1 flex min-h-0 overflow-hidden">
        <CatalogLeftPanel
          isOpen={isFilterSheetOpen}
          onClose={() => setFilterSheetOpen(false)}
          companyId={companyId ?? ''}
          filters={catalogSearch.filters}
          onUpdateFilter={catalogSearch.updateFilter}
          onResetFilters={catalogSearch.resetFilters}
          showSystemItemsCompanySetting={showSystemItems}
        />

        <div className="flex-1 flex flex-col min-h-0">
          <div className="flex-1 overflow-y-auto pb-20">
            <div className="p-3 md:p-4">
              <CatalogGrid
                items={catalogSearch.items}
                isLoading={catalogSearch.isLoading}
                isFetching={catalogSearch.isFetching}
                totalCount={catalogSearch.totalCount}
                totalPages={catalogSearch.totalPages}
                page={catalogSearch.page}
                onPageChange={catalogSearch.setPage}
                searchQuery={catalogSearch.filters.search}
                onSearchChange={catalogSearch.updateSearch}
                isSearchScopedToCategory={isSearchScopedToCategory}
                onToggleSearchScope={() => {
                  setIsSearchScopedToCategory(prev => !prev);
                  if (isSearchScopedToCategory) {
                    catalogSearch.updateFilter('browse_category_id', null);
                  }
                }}
                browseCategoryName={browseCategoryPath}
                onAddItem={handleAddItem}
                isAddingItem={isAddingItem}
                cartItemIds={cartItemIds}
                isLockedStatus={isLockedStatus}
                onOpenFilters={() => setFilterSheetOpen(!isFilterSheetOpen)}
                activeFiltersCount={activeFiltersCount}
                classificationMap={classificationMap}
              />
            </div>
          </div>

          {/* Cart Drawer — keep exactly as it was */}
          <Drawer open={isDrawerOpen} onOpenChange={setDrawerOpen}>
            <DrawerContent className="max-h-[85vh]">
              <DrawerHeader>
                <DrawerTitle className="flex items-center gap-2">
                  <ShoppingCart className="h-5 w-5" />
                  Estimate Items ({cartItemCount})
                </DrawerTitle>
              </DrawerHeader>
              <div className="px-4 pb-6 overflow-y-auto">
                <ShoppingCartSidebar
                  cartItems={displayLineItems || []}
                  onUpdateQuantity={handleUpdateQuantity}
                  onRemoveItem={handleRemoveItem}
                  onProceed={() => {
                    setDrawerOpen(false);
                    setShowFinalStep(true);
                  }}
                  isUpdating={isUpdatingQuantity}
                  isRemoving={isRemovingItem}
                  isOverlay
                />
              </div>
            </DrawerContent>
          </Drawer>
        </div>
      </div>
      
      {/* Mobile Floating Total Summary */}
      <FloatingTotalSummary
        itemCount={cartItemCount}
        totalAmount={cartTotal}
        onProceed={() => setShowFinalStep(true)}
        proceedLabel="Continue"
        showExpanded={summaryExpanded}
        onToggleExpanded={() => setSummaryExpanded(!summaryExpanded)}
        onCartOpen={() => setDrawerOpen(true)}
      />

      {/* Add Custom Item Dialog */}
      <AddCustomItemForm
        isOpen={showAddCustomItem}
        onClose={() => setShowAddCustomItem(false)}
        onSubmit={async (data) => {
          if (isLocalFirstMode) {
            addCustomItemLocally(data);
            notify.success('Custom item added to estimate');
          } else {
            await addCustomLineItem.mutateAsync(data);
          }
          setShowAddCustomItem(false);
        }}
        isSubmitting={!isLocalFirstMode && addCustomLineItem.isPending}
      />
    </div>
  );
};

export default EstimateLineItems;
